/* Guide des régies : lecture des données et fabrication du HTML.

   Source unique du rendu : le prérendu (scripts/prerender.js) et la page React
   appellent tous deux `guideRegiesHtml()`. Le HTML servi et le DOM après JavaScript
   sont donc le même, au caractère près.

   Aucun chiffre, aucune question, aucun repère, aucune recommandation, aucune légende
   n'est écrit ici : tout vient du CMS. Les seules chaînes du module sont les libellés
   fixes dictés par le cahier des charges — en-têtes de colonnes et libellés de boutons.
*/

export const CANAUX = ["tv_segmentee", "plateformes", "tv_lineaire", "dooh", "social"];
export const CONFIANCES = ["eleve", "moyen", "faible"];
export const TYPES_REPERE = ["releve", "marche", "cas_ecole"];
const POIDS = { eleve: 3, moyen: 2, faible: 1 };

const LIBELLES = {
  fil: "Guide SLIZ · mis à jour le",
  synthese: ["Régie · outil", "Ticket d'entrée", "Comment on achète", "Confiance"],
  reperes: ["Repère", "Montant", "Source", "Confiance"],
  cas: "Cas d'école",
  legende: "Indice de confiance",
  sources: "Autres sources consultées :",
  confiance: { eleve: "élevé", moyen: "moyen", faible: "faible" },
  reco: { estimer: "Tester cette enveloppe", brief: "Déposer un brief" },
  bande: {
    label: "Proposition sans engagement",
    texte: "Recommandation, concept et storyboard offerts. Vous décidez après.",
    cta: "Déposer mon brief",
  },
};

export const TITRE_SEO =
  "Guide des régies ouvertes aux PME : combien coûte une campagne, canal par canal | SLIZ";

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const fichesActives = (contenu) =>
  [...((contenu || {}).regies_guide || [])]
    .filter((f) => f && f.actif)
    .sort((a, b) => (a.ordre || 0) - (b.ordre || 0));

const reperesActifs = (fiche) => (fiche.reperes || []).filter((r) => r && r.actif);

/* Date de source : AAAA-MM-JJ en base, JJ/MM/AAAA à l'écran. */
export const dateCourte = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

/* Date de mise à jour du guide : AAAA-MM-JJ en base, « 7 octobre 2026 » à l'écran. */
export const dateLongue = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return "";
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    .format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])));
};

/* Confiance de la fiche au tableau de synthèse : le niveau le plus élevé parmi ses
   repères relevés. Aucun relevé, aucune mention. */
export const confianceFiche = (fiche) => {
  const niveaux = reperesActifs(fiche)
    .filter((r) => r.type === "releve" && POIDS[r.confiance])
    .map((r) => POIDS[r.confiance]);
  if (!niveaux.length) return "";
  const max = Math.max(...niveaux);
  return CONFIANCES.find((c) => POIDS[c] === max) || "";
};

const points = (niveau) => {
  const n = POIDS[niveau] || 0;
  if (!n) return "";
  const texte = LIBELLES.confiance[niveau];
  return `<span class="guide-points" role="img" aria-label="${LIBELLES.reperes[3]} : ${esc(texte)}">` +
    [1, 2, 3].map((i) => `<span class="guide-point-c${i <= n ? " plein" : ""}"></span>`).join("") +
    `</span>`;
};

const nombre = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/* Barre de fourchette d'un repère, à l'échelle de sa propre fiche. */
const echelleFiche = (fiche) => {
  const mins = reperesActifs(fiche).map((r) => nombre(r.montant_min)).filter((v) => v !== null);
  const maxs = reperesActifs(fiche).map((r) => nombre(r.montant_max)).filter((v) => v !== null);
  if (!mins.length || !maxs.length) return null;
  const bas = Math.min(...mins);
  const haut = Math.max(...maxs);
  return haut > bas ? { bas, haut } : null;
};

const barreFourchette = (repere, echelle) => {
  const min = nombre(repere.montant_min);
  const max = nombre(repere.montant_max);
  if (min === null || max === null || !echelle) return "";
  const pct = (v) => ((v - echelle.bas) / (echelle.haut - echelle.bas)) * 100;
  const debut = Math.max(0, Math.min(100, pct(min)));
  const largeur = Math.max(1, Math.min(100 - debut, pct(max) - debut));
  const fr = new Intl.NumberFormat("fr-FR");
  return `<tr class="guide-fourchette" data-testid="guide-fourchette-${esc(repere.libelle)}">` +
    `<td colspan="4"><span class="guide-fourchette-min">${esc(fr.format(min))}</span>` +
    `<span class="guide-rail"><span class="guide-segment" style="margin-left:${debut}%;width:${largeur}%"></span></span>` +
    `<span class="guide-fourchette-max">${esc(fr.format(max))}</span></td></tr>`;
};

const ligneRepere = (repere, echelle) => {
  const cas = repere.type === "cas_ecole";
  const source = repere.source_url
    ? `<a href="${esc(repere.source_url)}" target="_blank" rel="noopener">${esc(repere.source_nom)}</a>`
    : esc(repere.source_nom);
  const jour = dateCourte(repere.source_date);
  return `<tr${cas ? ' class="guide-cas"' : ""}>` +
    `<th scope="row">${cas ? `<em>${LIBELLES.cas}</em> ` : ""}${esc(repere.libelle)}</th>` +
    `<td class="guide-montant">${esc(repere.montant_texte)}</td>` +
    `<td>${source}${jour ? ` · ${jour}` : ""}</td>` +
    `<td>${points(repere.confiance)}</td></tr>` +
    barreFourchette(repere, echelle);
};

const ficheHtml = (fiche) => {
  const echelle = echelleFiche(fiche);
  const reperes = reperesActifs(fiche);
  const tableau = reperes.length
    ? `<div class="guide-cadre"><table class="guide-reperes" data-testid="guide-reperes-${esc(fiche.slug)}">` +
      `<thead><tr>${LIBELLES.reperes.map((c) => `<th scope="col">${c}</th>`).join("")}</tr></thead>` +
      `<tbody>${reperes.map((r) => ligneRepere(r, echelle)).join("")}</tbody></table></div>`
    : "";
  const reco = fiche.recommandation && fiche.recommandation.actif
    ? `<div class="guide-reco" data-testid="guide-reco-${esc(fiche.slug)}">` +
      `<p class="guide-reco-titre">${esc(fiche.recommandation.titre)}</p>` +
      `<p class="guide-reco-enveloppe">${esc(fiche.recommandation.enveloppe_texte)}</p>` +
      `<p class="guide-reco-texte">${esc(fiche.recommandation.texte)}</p>` +
      `<div class="guide-reco-ctas">` +
      `<a href="/estimer/" class="guide-btn-plein" data-testid="guide-reco-estimer-${esc(fiche.slug)}">${LIBELLES.reco.estimer}</a>` +
      `<a href="/brief/" class="guide-btn-contour" data-testid="guide-reco-brief-${esc(fiche.slug)}">${LIBELLES.reco.brief}</a>` +
      `</div></div>`
    : "";
  const cle = fiche.fiche_liste
    ? ""
    : `<p class="guide-prefixe">${esc(fiche.prefixe_cle)}</p>` +
      `<p class="guide-cle">${esc(fiche.chiffre_cle)}</p>`;
  return `<article class="guide-fiche" id="fiche-${esc(fiche.slug)}" data-testid="guide-fiche-${esc(fiche.slug)}">` +
    `<h3 class="guide-question">${esc(fiche.question)}</h3>${cle}${tableau}${reco}</article>`;
};

const barreTicket = (fiche, plafond) => {
  const v = nombre(fiche.ticket_montant);
  if (v === null || !plafond) return "";
  const largeur = Math.max(1, Math.min(100, (v / plafond) * 100));
  return `<span class="guide-rail guide-rail-ticket">` +
    `<span class="guide-segment" style="width:${largeur}%"></span></span>`;
};

const sectionCanal = (canal, reglage, fiches, plafond) => {
  const def = (reglage.canaux || {})[canal] || {};
  const lignes = fiches
    .map((f) =>
      `<tr data-testid="guide-ligne-${esc(f.slug)}">` +
      `<th scope="row">${esc(f.nom)}${f.sous_titre ? `<span>${esc(f.sous_titre)}</span>` : ""}</th>` +
      `<td><strong>${esc([f.prefixe_cle, f.chiffre_cle].filter(Boolean).join(" "))}</strong>` +
      `${barreTicket(f, plafond)}</td>` +
      `<td>${esc(f.achat)}</td><td>${points(confianceFiche(f))}</td></tr>`)
    .join("");
  return `<section class="guide-canal" id="${canal}" data-testid="guide-canal-${canal}">` +
    `<h2 class="guide-h2">${esc(def.libelle)}</h2>` +
    (def.chapo ? `<p class="guide-chapo-canal">${esc(def.chapo)}</p>` : "") +
    `<div class="guide-cadre"><table class="guide-synthese" data-testid="guide-synthese-${canal}">` +
    `<thead><tr>${LIBELLES.synthese.map((c) => `<th scope="col">${c}</th>`).join("")}</tr></thead>` +
    `<tbody>${lignes}</tbody></table></div>` +
    fiches.map(ficheHtml).join("") +
    `</section>`;
};

/* Corps complet de la page, en-tête et bande finale comprises. */
export function guideRegiesHtml(reglage, fiches, ctas) {
  const plafond = Math.max(0, ...fiches.map((f) => nombre(f.ticket_montant) || 0));
  const sections = CANAUX
    .map((canal) => [canal, fiches.filter((f) => f.canal === canal)])
    .filter(([, liste]) => liste.length > 0)
    .map(([canal, liste]) => sectionCanal(canal, reglage, liste, plafond))
    .join("");

  const bloc = (cle, testid) => {
    const b = reglage[cle] || {};
    if (!b.titre && !b.texte) return "";
    return `<section class="guide-double" data-testid="${testid}">` +
      `<h2 class="guide-h2">${esc(b.titre)}</h2><p>${esc(b.texte)}</p></section>`;
  };

  const legendes = CONFIANCES
    .map((n) => [n, reglage[`legende_${n}`]])
    .filter(([, texte]) => texte)
    .map(([n, texte]) => `<li>${points(n)}<span>${esc(texte)}</span></li>`)
    .join("");

  const jour = dateLongue(reglage.date_maj);
  return `<main class="guide" data-testid="guide-regies-page">` +
    `<header class="guide-entete">` +
    (jour ? `<p class="guide-fil"><span class="guide-pulse" aria-hidden="true"></span>${LIBELLES.fil} ${esc(jour)}</p>` : "") +
    `<h1 class="guide-titre">${esc(reglage.titre)}</h1>` +
    (reglage.chapo ? `<p class="guide-chapo">${esc(reglage.chapo)}</p>` : "") +
    `<div class="guide-ctas">` +
    ctas.map((c, i) =>
      `<a href="${c.to}" class="${i === 0 ? "guide-btn-nuit" : "guide-btn-ligne"}" data-testid="${c.testid}">${esc(c.l)}</a>`).join("") +
    `</div></header>` +
    sections +
    (bloc("bloc_pas_besoin", "guide-bloc-pas-besoin") || bloc("bloc_utiles", "guide-bloc-utiles")
      ? `<div class="guide-doubles">${bloc("bloc_pas_besoin", "guide-bloc-pas-besoin")}${bloc("bloc_utiles", "guide-bloc-utiles")}</div>`
      : "") +
    (legendes
      ? `<section class="guide-legende" data-testid="guide-legende"><p class="guide-label">${LIBELLES.legende}</p><ul>${legendes}</ul></section>`
      : "") +
    (reglage.note_methode || reglage.sources_sans_lien
      ? `<section class="guide-methode" data-testid="guide-methode">` +
        (reglage.note_methode ? `<p>${esc(reglage.note_methode)}</p>` : "") +
        (reglage.sources_sans_lien ? `<p>${LIBELLES.sources} ${esc(reglage.sources_sans_lien)}</p>` : "") +
        `</section>`
      : "") +
    `<aside class="guide-bande" data-testid="guide-bande-finale">` +
    `<p class="guide-label">${LIBELLES.bande.label}</p>` +
    `<p class="guide-bande-texte">${LIBELLES.bande.texte}</p>` +
    `<a href="/brief/" class="guide-btn-plein" data-testid="guide-bande-cta">${LIBELLES.bande.cta}</a>` +
    `</aside></main>`;
}

export const articleJsonLd = (reglage, url, orgId) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: reglage.titre,
  description: reglage.meta_description,
  ...(reglage.date_maj ? { dateModified: reglage.date_maj } : {}),
  publisher: { "@id": orgId },
  mainEntityOfPage: url,
});

/* FAQPage : une question par fiche active, réponse composée du chiffre clé, des
   repères actifs et de la recommandation si elle est active. */
export const faqJsonLd = (fiches) => {
  if (!fiches.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: fiches.map((f) => {
      const morceaux = [];
      if (f.chiffre_cle) morceaux.push(`${[f.prefixe_cle, f.chiffre_cle].filter(Boolean).join(" ")}.`);
      for (const r of reperesActifs(f)) {
        morceaux.push(`${r.libelle} : ${r.montant_texte} (${r.source_nom}, ${dateCourte(r.source_date)}).`);
      }
      if (f.recommandation && f.recommandation.actif) morceaux.push(f.recommandation.texte);
      return {
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: morceaux.join(" ") },
      };
    }),
  };
};
